/**
 * Pure (no Svelte imports) autoplay state machine.
 *
 * Walks `years` in order using getYear/setYear callbacks so the caller
 * keeps the year as a single source of truth. Pauses `endPause` ms when
 * reaching the last year before looping back to the first.
 *
 * Why a controller (and not a custom $effect inside Chart.svelte)?
 *   - Centralises every timer (interval + final-pause timeout) so we
 *     can guarantee they're cleared on destroy / on visibility change.
 *   - Survives reactivity changes: refs (years, getter, setter) can be
 *     updated via `setRefs` whenever the data reloads without recreating
 *     timers.
 *   - Easy to mock in unit tests.
 *
 * Lifecycle:
 *   const ctrl = createAutoplayController({ step, endPause });
 *   ctrl.setRefs(years, () => year, (y) => year = y);
 *   ctrl.start();          // begins ticking
 *   ctrl.stop();           // hard pause + clear timers
 *   ctrl.notifyUserChange();  // user dragged the slider — reset cadence,
 *                             // cancel any pending end-of-loop pause
 *   ctrl.destroy();        // alias of stop() for the parent's cleanup
 */
export function createAutoplayController({ step, endPause }) {
  let stepTimer = null;
  let endPauseTimer = null;
  /** `true` once the controller has been started and not yet stopped. */
  let running = false;
  let years = [];
  let getYear = () => null;
  let setYear = () => {};

  function clearStepTimer() {
    if (stepTimer != null) {
      clearInterval(stepTimer);
      stepTimer = null;
    }
  }

  function clearEndPauseTimer() {
    if (endPauseTimer != null) {
      clearTimeout(endPauseTimer);
      endPauseTimer = null;
    }
  }

  function startStepInterval() {
    clearStepTimer();
    stepTimer = setInterval(tick, step);
  }

  function tick() {
    if (!years.length) return;
    const cur = getYear();
    const i = years.indexOf(cur);
    // If we're at (or past) the end, swap the interval for a one-shot
    // pause and only then loop back. Without this branch you'd get
    // either a stutter (immediate reset) or two competing timers.
    if (i < 0 || i >= years.length - 1) {
      clearStepTimer();
      endPauseTimer = setTimeout(() => {
        endPauseTimer = null;
        if (!running) return;
        setYear(years[0]);
        startStepInterval();
      }, endPause);
      return;
    }
    setYear(years[i + 1]);
  }

  return {
    setRefs(yrs, get, set) {
      years = Array.isArray(yrs) ? yrs : [];
      getYear = typeof get === 'function' ? get : () => null;
      setYear = typeof set === 'function' ? set : () => {};
    },
    start() {
      if (running) return;
      running = true;
      // Don't tick immediately; the user just became eligible, so honour
      // a full `step` of latency before the first auto-advance.
      startStepInterval();
    },
    stop() {
      running = false;
      clearStepTimer();
      clearEndPauseTimer();
    },
    notifyUserChange() {
      // Cancel any pending end-of-loop pause so the user's chosen year
      // becomes the new starting point of the cadence.
      clearEndPauseTimer();
      // Reset the interval so the next auto-tick fires `step` ms after
      // the manual change (rather than mid-step, which feels jittery).
      if (running) startStepInterval();
    },
    destroy() {
      running = false;
      clearStepTimer();
      clearEndPauseTimer();
    }
  };
}
